// ─────────────────────────────────────────────────────────────────────────────
// WP-08 / C2 — Reportería de Reconocimiento (front). SEIS pestañas: Reco Diario · Brillamos Juntos ·
// Demográfico · Históricos · Líderes · Moderación. Cuatro salen de v1/analytics/recognition/*;
// Moderación vive en RecognitionService (v1/moderation/report) e Históricos (id interno `campaigns`,
// antes «Campañas General») es propia (RF-151, sin filtros superiores).
//
// 2026-09-29 — DESVIACIÓN CONSCIENTE de la maqueta y de la Sección 7 de la Especificación: eran SIETE.
// El «Históricos» anterior se eliminó y «Campañas General» pasó a llamarse «Históricos», en su mismo
// lugar (pedido del cliente, documento de Adriana). Inventario y rollback:
// `docs/contexto/historicos-eliminado-rollback.md`.
//
// Eran NUEVE: `Heatmap` y `Red ONA` tenían pestaña propia y la maqueta no las lista. Su contenido
// NO se perdió — en la maqueta vive dentro del panel Demográfico, y aquí también.
// Reutiliza el shell de tabs y el FilterBar del AnalyticsView (rango + OU + fundamentales) y el
// export Excel del módulo. SIN puntos (regla de oro). Estados honestos por tab.
//
// Se monta en dos lugares (mismo componente, sin duplicar):
//   1. Ruta directa `/app/reportes` (RecognitionAnalyticsView, con RoleGuard — providers y sidebar
//      los aporta <AppLayout/> desde la FASE 3, ver CONTRATO-RUTAS.md).
//   2. Sección Admin "Reportería" (REQ-133) → <RecognitionReports /> dentro del shell de admin
//      (mismo <AppLayout/>, con su propio RoleGuard).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RoleGuard, useEffectiveRoles } from '@/auth/RoleGuard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useRecognitionCatalog, useSites, useCampaigns, getFundamentalName, getRecognitionErrorStatus } from '@/api/recognition';
import type { RecognitionReportParams } from '@/api/recognition';
import { DEFAULT_FILTERS } from '@/views/AnalyticsView/hooks/useAnalyticsFilters';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { useOuScope } from '@/hooks/useOuScope';
import { useOUItems } from '@/hooks/useOUs';
import { useAdminOuFilter } from '../admin/useAdminOuFilter';
import { SelectorCampanaReporte } from './SelectorCampanaReporte';
import type { ErrorDeCargaCampanas } from './SelectorCampanaReporte';
import {
  TODAS_LAS_CAMPANAS, alcanceNivelMasAlto, campanaCombinada, campanasDeTodas, campanasDelCombo, ousDeLaBarraParaCampana,
  ousFrenteAlAlcance,
} from './campanasReporte';
import type { EstadoCampanaReporte } from './campanasReporte';
import { PageSpinner } from '@/components/common/PageSpinner';
import { useRecognitionTabFilters } from './useRecognitionTabFilters';
import { FilterTags } from './FilterTags';
import { BloquePlantilla } from './BloquePlantilla';
import { BannersDeContexto, RecorteMostrado } from './BannersDeContexto';
import { BannerHomologacion } from './BannerHomologacion';
import { TiraDeFiltros } from './TiraDeFiltros';
import { isoADma } from './CampoFecha';
import type { FilterTag } from './FilterTags';
import { DailyTab } from './tabs/DailyTab';
import { cubreTodoElAlcance, ousDeRecibidos } from './ousDeRecibidos';
import { ShineTab } from './tabs/ShineTab';
import { HeatmapTab } from './tabs/HeatmapTab';
import { NetworkTab } from './tabs/NetworkTab';
import { GenerationalTab } from './tabs/GenerationalTab';
import { DemograficoExportButton } from './tabs/DemograficoExportButton';
import { LeadersTab } from './tabs/LeadersTab';
import { ModerationReportTab } from './tabs/ModerationReportTab';
import { CampaignsGeneralTab } from './tabs/CampaignsGeneralTab';
import { HEATMAP_ALLOWED_ROLES } from './reportsShared';
import '@/views/AnalyticsView/AnalyticsView.css';
import './RecognitionReports.css';

/**
 * C2 — las de la maqueta (seis desde 2026-09-29). `heatmap` y `network` salen del tipo a propósito, y no solo de la
 * barra: dejándolos, una ruta con `?tab=heatmap` seguiría compilando y pintaría una pestaña que la
 * maqueta no tiene. Su contenido no se pierde — vive dentro de Demográfico.
 */
type ReportTab =
  | 'daily' | 'shine' | 'generational' | 'leaders'
  | 'moderation' | 'campaigns';

/**
 * D-13 / P12 — las pestañas que NO consumen la barra de filtros.
 *
 * La maqueta no la oculta en ninguna (`switchRepTab` solo togglea paneles), así que esto es una
 * desviación consciente. El motivo de cada una, medido, porque «coherencia técnica» no era motivo:
 *
 *   · (`history` estuvo aquí hasta el 2026-09-29, cuando se eliminó esa pestaña.)
 *   · `campaigns`  — sus cifras salen de `useCampaignSummaries`, que consulta cada campaña en SU
 *                    propia vigencia y con el alcance del rol. Ni el rango ni la OU de la barra
 *                    entran en esa consulta, así que la barra se vería y no haría nada.
 *
 * 🔴 La regla: **un filtro que no filtra es peor que no tener barra.** Si pasa a consumirla, se
 * quita de aquí — no se deja «por si acaso».
 *
 * `moderation` estuvo en esta lista y SALIÓ el 2026-09-04: su endpoint ya acepta OU, rango y
 * persona, así que ocultarle la barra entera dejaba la bandeja de casos sin forma de acotarse. El
 * único filtro que no honra es Fundamental, y eso lo declara el propio panel con
 * `AvisoFiltroNoAplica` — ocultar los cuatro controles por uno que no aplica era demasiado.
 */
const TABS_WITHOUT_FILTER_BAR: ReadonlySet<ReportTab> = new Set<ReportTab>([
  'campaigns',
]);

/**
 * 2026-09-24 — requerimiento «Visibilidad y lógica del filtro de Site»: el Site se ve y se aplica
 * SOLO en Reconocimiento Diario, Brillamos y Demográfico. En Moderación, Líderes e Históricos
 * el selector se oculta Y el valor no viaja en `params`: ocultarlo pero seguir mandándolo
 * filtraría en silencio una pestaña que no lo muestra.
 *
 * Cada una de las tres lo aplica de verdad en el backend (AnalyticsService: daily, campaign,
 * campaign-live, campaign-summary, heatmap, network, generational, generational-distribution y la
 * brecha de Pulso). Si una pestaña deja de honrarlo, sale de aquí — no se deja «por si acaso».
 */
const TABS_CON_SITE: ReadonlySet<ReportTab> = new Set<ReportTab>(['daily', 'shine', 'generational']);

/**
 * C2 — las pestañas de Reportería, en su orden. La maqueta (`switchRepTab` en
 * `fundamentales-admin.js`) trae SIETE: Reco Diario · Brillamos Juntos · Históricos · Demográfico ·
 * Campañas General · Líderes · Moderación. Desde el 2026-09-29 son SEIS: Reco Diario · Brillamos
 * Juntos · Demográfico · Históricos (antes «Campañas General», en su mismo lugar) · Líderes ·
 * Moderación.
 *
 * `heatmap` y `network` DEJAN de ser pestañas: en la maqueta su contenido vive dentro del panel
 * Demográfico (el mapa «OU × Fundamental» y la tarjeta de colaboración cross-OU están los dos en
 * `rep-panel-demografico`). No se borra nada — se mueve, que es lo que pide la regla 1:1.
 */
/**
 * Paridad visual Fase 8 (2026-09-11) — íconos calcados de la maqueta (`fund-puntos-tabs`, emoji
 * 🏅⭐📊👥🎯👤🚨), a su equivalente más cercano en FontAwesome (el set que ya usa toda la app; no
 * se introduce una fuente de emoji nueva). `fa-star` ya coincidía; Demográfico/Líderes/Moderación
 * se dejan igual — ya transmiten el mismo concepto (grupo de personas / persona / alerta) y no hay
 * un ícono FA idéntico al emoji.
 */
export const TABS: Array<{ id: ReportTab; icon: string; labelKey: string }> = [
  { id: 'daily',        icon: 'fa-medal',              labelKey: 'reports.tabs.daily'        },
  { id: 'shine',        icon: 'fa-star',               labelKey: 'reports.tabs.shine'        },
  { id: 'generational', icon: 'fa-people-group',       labelKey: 'reports.tabs.generational' },
  { id: 'campaigns',    icon: 'fa-bullseye',           labelKey: 'reports.tabs.campaigns'   },
  { id: 'leaders',      icon: 'fa-user-tie',           labelKey: 'reports.tabs.leaders'      },
  { id: 'moderation',   icon: 'fa-triangle-exclamation', labelKey: 'reports.tabs.moderation' },
];

/**
 * Contenido de la reportería (FilterBar + tabs). Se reutiliza tal cual dentro del shell de Admin
 * (REQ-133) y bajo la ruta directa. No incluye providers ni guard: los aporta cada contenedor.
 */
export function RecognitionReports() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const [activeTab, setActiveTab] = useState<ReportTab>('daily');
  // Regla 2026-09-23 — Demográfico es EXCLUSIVO de Admin General: ningún otro rol (tampoco
  // AdminBrillamos ni el Admin de OU) ve la pestaña. Por la lista completa de roles, no por el
  // primario: un multi-rol con AdminGlobal sí la conserva.
  const roles = useEffectiveRoles();
  const puedeVerDemografico = roles.includes('AdminGlobal');
  const tabsVisibles = useMemo(
    () => TABS.filter((tab) => tab.id !== 'generational' || puedeVerDemografico),
    [puedeVerDemografico],
  );
  // Matriz de permisos de Reconocimiento — "Exportar datos" (RN-K03) es independiente de "Ver
  // reportería": quien entra a esta pantalla (ya autorizado por el RoleGuard de arriba) puede no
  // tener el permiso granular de exportación. `?.` en cascada: mientras el perfil resuelve,
  // `recognitionPermissions` es `null`/`undefined` y el gate falla CERRADO (botones ocultos), nunca
  // abierto por defecto.
  const currentUser = useCurrentUser();
  const canExportData = currentUser?.recognitionPermissions?.includes('exportData') ?? false;
  // D-13 — barra de filtros COMPARTIDA por todas las pestañas: la maqueta la deja fuera de los
  // paneles y no la reinicia al cambiar de tab, así que los filtros se heredan.
  const { filters, setFilter, resetFilters, resetFilter } = useRecognitionTabFilters(activeTab);
  const { resolveOuName } = useOuNameResolver();
  // 'brillamos': el alcance de ESTE módulo, no el de Pulso — ver el doc-comment de `useOuScope`.
  const { isResolving, hasEmptyScope, resolveQueryOuIdsMulti, scopedOuItems, allowedOuIds } = useOuScope('brillamos');

  /*
   * BJ-F7 (War Room 2026-09-21, transcripción 3, Rubi 3:15) — el Admin de OU ve la Reportería SOLO
   * de su OU: el selector arranca con su OU y, si solo tiene una, queda bloqueado. Mismo hook que
   * Moderación/Ganadores/Vistas. La selección efectiva se DERIVA: sin selección guardada, su OU.
   */
  const ouFilter = useAdminOuFilter();
  const ouElegidas = useMemo<readonly string[]>(() => {
    if (!ouFilter.isOuRestricted || !ouFilter.defaultOuId) return filters.ou;
    if (ouFilter.isLocked || filters.ou.length === 0) return [ouFilter.defaultOuId];
    return filters.ou;
  }, [ouFilter.isOuRestricted, ouFilter.isLocked, ouFilter.defaultOuId, filters.ou]);

  /** Nombres de las OUs SELECCIONADAS (nunca la lista expandida con descendientes) para tags/banners. */
  const ouNombresSeleccionados = useMemo(
    () => ouElegidas.map(resolveOuName).join(', '),
    [ouElegidas, resolveOuName],
  );

  const { data: ouItemsCompletos = [] } = useOUItems({ activeOnly: false, rootsOnly: false });

  /**
   * Lista expandida (selección + descendientes, intersectada con el alcance) para las consultas.
   * 2026-10-27 — con TODAS las OUs del alcance marcadas no se manda OU, igual que con «Todas las OUs»:
   * filtrar por OU deja fuera a quien envía sin OU (13 sin filtro vs. 10 con las 6 marcadas).
   */
  const ouIdsExpandidos = useMemo(
    () => (cubreTodoElAlcance(ouElegidas, ouItemsCompletos, scopedOuItems)
      ? undefined
      : resolveQueryOuIdsMulti(ouElegidas)),
    [ouElegidas, ouItemsCompletos, scopedOuItems, resolveQueryOuIdsMulti],
  );

  const { data: catalog } = useRecognitionCatalog();

  /*
   * DEM-F2 (REPORTERIA-REGLAS §10, 2026-09-28) — Demográfico no se filtra por persona: una
   * distribución por generación con una sola persona deja de ser una distribución. El buscador se
   * oculta en esa pestaña y la persona ni viaja a sus consultas ni aparece como chip. La elección se
   * CONSERVA en la barra: al volver a otra pestaña sigue puesta.
   */
  const personaAplica = activeTab !== 'generational';
  const personaEfectiva = personaAplica ? filters.persona : null;

  // Site — solo en las pestañas de `TABS_CON_SITE`. La lista son los Sites de las personas de las OUs
  // ELEGIDAS (sin expandir: el subárbol lo resuelve UserAdminService), o los del alcance si no hay
  // selección. `siteEfectivo` descarta un Site que ya no está en la lista (p. ej. tras cambiar la OU):
  // mandarlo dejaría el reporte en cero por un filtro que el selector ya no muestra.
  const siteVisible = TABS_CON_SITE.has(activeTab);
  const sitesQuery = useSites(ouElegidas, siteVisible);
  const sitesDelFiltro = useMemo(
    () => (sitesQuery.data ?? []).map((site) => ({ id: site.id, nombre: site.name })),
    [sitesQuery.data],
  );
  const siteEfectivo = siteVisible && filters.site && sitesDelFiltro.some((site) => site.id === filters.site)
    ? filters.site
    : '';
  const siteNombre = sitesDelFiltro.find((site) => site.id === siteEfectivo)?.nombre ?? siteEfectivo;

  /** Lo único que la tira necesita del catálogo: id y nombre ya resuelto al idioma. */
  const fundamentalesDelFiltro = useMemo(
    () => (catalog ?? []).map((f) => ({ id: f.id, nombre: getFundamentalName(f, lang) })),
    [catalog, lang],
  );

  /*
   * 2026-10-01 — Top Reconocidos y la hoja «Todos los reconocimientos Recibidos» de Brillamos Juntos:
   * sin OU en la barra, la OU a la que pertenece el administrador (con sus descendientes).
   */
  const ouDelAdmin = currentUser?.ouId;
  const rankingOuIds = useMemo(
    () => (ouElegidas.length === 0 && ouDelAdmin ? resolveQueryOuIdsMulti([ouDelAdmin]) : undefined),
    [ouElegidas, ouDelAdmin, resolveQueryOuIdsMulti],
  );

  const params = useMemo<RecognitionReportParams>(
    () => ({
      ouIds: ouIdsExpandidos,
      rankingOuIds: rankingOuIds?.length ? [...rankingOuIds] : undefined,
      startDate: filters.dateFrom || undefined,
      endDate: filters.dateTo || undefined,
      fundamentalId: filters.fundFilter || undefined,
      personaId: personaEfectiva?.id,
      site: siteEfectivo || undefined,
      language: lang,
    }),
    [ouIdsExpandidos, rankingOuIds, filters.dateFrom, filters.dateTo, filters.fundFilter, personaEfectiva, siteEfectivo, lang],
  );

  /*
   * Brillamos Juntos — campaña y estatus en la BARRA (BJ-D3/BJ-F4, REPORTERIA-REGLAS.md §8). Viven
   * aquí y no en `ShineTab` porque el selector va al principio de la tira compartida.
   *   · Estatus Activa por omisión: se elige uno u otro, nunca mezclados.
   *   · Cascada (BJ-F3): el combo solo ofrece campañas del estatus elegido que apliquen a las OUs
   *     de arriba, leyendo el campo de alcance de la campaña.
   *   · Una campaña que deja de estar en el combo (cambió la OU o el estatus) se descarta en vez de
   *     seguir mandándose: el reporte quedaría acotado a algo que la barra ya no muestra.
   */
  const esBrillamos = activeTab === 'shine';
  const [estadoCampana, setEstadoCampana] = useState<EstadoCampanaReporte>('Activa');
  const [campaignIdElegida, setCampaignIdElegida] = useState('');
  const campaigns = useCampaigns(undefined, 1, 100, esBrillamos);
  // 403 (sin capacidad para listar campañas) o cualquier otro fallo: el combo lo dice en vez de
  // aparentar que no hay campañas (pase de contexto QA 2026-10-22).
  let errorDeCampanas: ErrorDeCargaCampanas = null;
  if (campaigns.isError) {
    errorDeCampanas = getRecognitionErrorStatus(campaigns.error) === 403 ? 'sinPermiso' : 'error';
  }
  /** 2026-10-26 — OUs de «Reconocimientos Recibidos» del Excel del Diario; `undefined` = hoja sin registros. */
  const ouIdsRecibidos = useMemo(
    () => ousDeRecibidos(ouElegidas, ouItemsCompletos, scopedOuItems),
    [ouElegidas, ouItemsCompletos, scopedOuItems],
  );
  const campanasCombo = useMemo(
    () => campanasDelCombo(campaigns.data?.items ?? [], estadoCampana, ouElegidas, ouItemsCompletos),
    [campaigns.data, estadoCampana, ouElegidas, ouItemsCompletos],
  );
  /*
   * BJT-1 (2026-10-14, «Bugs Brillamos2.docx») — «Todas las campañas»: las campañas Brillamos del combo
   * se reportan como una campaña COMBINADA (alcance = unión, vigencia = de la primera a la última); las
   * consultas mandan la lista de ids (`idsDeTodas`). Con «Todas las OUs», «OUs activas» es la unión.
   */
  const idsDeTodas = useMemo(
    () => (campaignIdElegida === TODAS_LAS_CAMPANAS ? campanasDeTodas(campanasCombo).map((c) => c.id) : undefined),
    [campaignIdElegida, campanasCombo],
  );
  const campanaElegida = useMemo(
    () => (campaignIdElegida === TODAS_LAS_CAMPANAS
      ? campanaCombinada(campanasDeTodas(campanasCombo), t('reports.shine.allCampaigns'))
      : campanasCombo.find((c) => c.id === campaignIdElegida) ?? null),
    [campaignIdElegida, campanasCombo, t],
  );

  /*
   * D1 (2026-09-30) — al elegir una campaña ACTIVA, el rango de la barra se llena con su inicio y su
   * fin: por omisión el reporte cuenta la campaña completa y, si el usuario cambia las fechas, se
   * acotan a la vez las tarjetas, el ranking, la adopción y el Excel. Antes quedaba la semana en curso, y las
   * tarjetas —que contaban la campaña completa— no cuadraban con el ranking.
   *
   * Una campaña FINALIZADA no toca el rango: se reporta completa y las fechas no aplican (BJ-F5).
   *
   * 2026-10-27 (reunión) — la OU de la barra también sigue a la campaña (`ousDeLaBarraParaCampana`):
   * la hoja de Recibidos del Excel se pide por esa OU y se quedaba con la de la campaña anterior.
   * «Todas las campañas» y la OU fija del Admin de OU no la tocan.
   */
  const elegirCampana = useCallback((id: string) => {
    setCampaignIdElegida(id);
    const campana = id === TODAS_LAS_CAMPANAS
      ? campanaCombinada(campanasDeTodas(campanasCombo), '')
      : campanasCombo.find((c) => c.id === id);
    if (!campana) return;
    if (id !== TODAS_LAS_CAMPANAS && !ouFilter.isLocked) {
      const ous = ousDeLaBarraParaCampana(campana.alcance ?? [], ouElegidas, ouItemsCompletos, allowedOuIds);
      if (ous) setFilter('ou', ous);
    }
    if (campana.estado === 'Finalizada' || !campana.fechaInicio) return;
    setFilter('dateFrom', campana.fechaInicio.slice(0, 10));
    if (campana.fechaFin) setFilter('dateTo', campana.fechaFin.slice(0, 10));
  }, [campanasCombo, setFilter, ouFilter.isLocked, ouElegidas, ouItemsCompletos, allowedOuIds]);
  const alcanceCampana = useMemo(
    () => (campanaElegida ? alcanceNivelMasAlto(campanaElegida.alcance ?? [], ouItemsCompletos) : []),
    [campanaElegida, ouItemsCompletos],
  );

  /*
   * BUG-6 (sesión 2026-10-02) — al elegir la campaña las fechas cambian solas (D1) y en la sesión se
   * leyó como un error. Se avisa mientras el rango sea exactamente la vigencia de la campaña.
   */
  const fechasDeLaCampana = Boolean(
    esBrillamos && campanaElegida && campanaElegida.estado !== 'Finalizada' && campanaElegida.fechaInicio
    && filters.dateFrom === campanaElegida.fechaInicio.slice(0, 10)
    && (!campanaElegida.fechaFin || filters.dateTo === campanaElegida.fechaFin.slice(0, 10)),
  );

  /*
   * BUG-3 (sesión 2026-10-02) — sin OU en la barra, «Mostrando» decía «toda la organización» mientras
   * el reporte de la campaña mostraba solo su alcance (p. ej. FOSE). Con campaña elegida, «Mostrando»
   * dice el alcance de la campaña, que es lo que de verdad acota las cifras.
   */
  /*
   * BJF-1 (2026-10-14, «Bugs Brillamos2.docx») — el reporte es POR CAMPAÑA y manda su alcance
   * (BJ-F2): con una campaña de FOSE, elegir «Todas las OUs» o las seis OUs da las mismas cifras que
   * FOSE. Se separan las OUs elegidas en las que la campaña cubre y las que no; `null` = sin campaña
   * o campaña global, donde la OU de la barra manda tal cual.
   */
  const frenteAlAlcance = useMemo(() => {
    if (!esBrillamos || !campanaElegida || (campanaElegida.alcance ?? []).length === 0) return null;
    return ousFrenteAlAlcance(campanaElegida.alcance ?? [], ouElegidas, ouItemsCompletos);
  }, [esBrillamos, campanaElegida, ouElegidas, ouItemsCompletos]);

  const recorteDeOu = useMemo(() => {
    const textoDelAlcance = (ids: readonly string[]) => `${t('reports.shine.campaignScope')} ${ids.map(resolveOuName).join(', ')}`;
    // Con OUs elegidas que la campaña no cubre, «Mostrando» dice las que de verdad se cuentan.
    if (frenteAlAlcance && (ouElegidas.length === 0 || frenteAlAlcance.fuera.length > 0)) {
      return textoDelAlcance(frenteAlAlcance.efectivas);
    }
    if (ouElegidas.length > 0) return ouNombresSeleccionados;
    if (!esBrillamos || !campanaElegida) return null;
    return `${t('reports.shine.campaignScope')} ${t('reports.common.scopeAll')}`;
  }, [frenteAlAlcance, ouElegidas, ouNombresSeleccionados, esBrillamos, campanaElegida, resolveOuName, t]);

  /** BJF-1 — aviso del dashboard: la campaña solo cubre su alcance y qué OUs elegidas quedan fuera. */
  const avisoDeAlcance = useMemo(() => {
    if (!frenteAlAlcance) return null;
    const alcance = alcanceCampana.map(resolveOuName).join(', ');
    if (ouElegidas.length === 0) return t('reports.shine.scopeOnlyCoversAll', { alcance });
    if (frenteAlAlcance.fuera.length === 0) return null;
    return t('reports.shine.scopeOnlyCoversSome', { alcance, fuera: frenteAlAlcance.fuera.map(resolveOuName).join(', ') });
  }, [frenteAlAlcance, alcanceCampana, ouElegidas, resolveOuName, t]);

  // La barra es única y compartida (D-13), pero tres pestañas no la consumen: en ellas se oculta,
  // porque un tag que el panel ignora es peor que no tener barra.
  const showFilterBar = !TABS_WITHOUT_FILTER_BAR.has(activeTab);

  // Tags removibles individuales: uno por filtro activo, cada uno quita solo su propio campo
  // (resetFilter) sin tocar el resto de la barra.
  const filterTags = useMemo<FilterTag[]>(() => {
    const tags: FilterTag[] = [];
    if (ouElegidas.length > 0) {
      tags.push({
        key: 'ou',
        label: `${t('reports.filters.ou')}: ${ouNombresSeleccionados}`,
        // Admin de OU con la OU fija: su chip no se quita (quitarlo no haría nada).
        onRemove: ouFilter.isLocked ? undefined : () => resetFilter('ou'),
      });
    }
    if (siteEfectivo) {
      tags.push({
        key: 'site',
        label: `${t('reports.filters.site')}: ${siteNombre}`,
        onRemove: () => resetFilter('site'),
      });
    }
    if (filters.fundFilter) {
      const fund = catalog?.find((f) => f.id === filters.fundFilter);
      tags.push({
        key: 'fundFilter',
        label: `${t('reports.filters.fundamental')}: ${fund ? getFundamentalName(fund, lang) : filters.fundFilter}`,
        onRemove: () => resetFilter('fundFilter'),
      });
    }
    if (personaEfectiva) {
      tags.push({
        key: 'persona',
        label: `${t('reports.filters.person')}: ${personaEfectiva.displayName}`,
        onRemove: () => resetFilter('persona'),
      });
    }
    /*
     * 🔴 El rango SIEMPRE se enseña, aunque sea el de por omisión.
     *
     * Antes solo salía si difería del defecto, y como el defecto es la semana en curso, en la práctica
     * NUNCA aparecía: la pantalla mostraba «Fundamental: X» y «OU: Y» como filtros puestos y callaba el
     * recorte que más manda sobre las cifras. Alguien que mira un total sin ver el rango no sabe de
     * cuándo es ese total.
     *
     * La «×» solo aparece cuando hay algo que quitar. En el valor por omisión no se pinta: quitar un
     * rango no puede dejar el reporte sin fechas —el backend exige los dos extremos juntos— así que
     * «quitar» solo puede significar «volver a la semana en curso», y ofrecer eso cuando ya estás en
     * la semana en curso es un botón que no hace nada.
     */
    const rangoPorOmision =
      filters.dateFrom === DEFAULT_FILTERS.dateFrom && filters.dateTo === DEFAULT_FILTERS.dateTo;

    tags.push({
      key: 'dateRange',
      // BJS-4 — en día/mes/año, igual que los campos de fecha.
      label: `${t('reports.filters.dateRange')}: ${isoADma(filters.dateFrom)} – ${isoADma(filters.dateTo)}`,
      onRemove: rangoPorOmision
        ? undefined
        : () => {
          resetFilter('dateFrom');
          resetFilter('dateTo');
        },
    });
    return tags;
  }, [
    ouElegidas, ouFilter.isLocked, ouNombresSeleccionados, siteEfectivo, siteNombre, filters.fundFilter, personaEfectiva,
    filters.dateFrom, filters.dateTo, catalog, lang, resetFilter, t,
  ]);

  /**
   * Los filtros que Moderación SÍ aplica, con los nombres ya resueltos, para que su Excel los
   * declare. Se derivan de los MISMOS chips que pinta la pantalla —menos Fundamental, el único que
   * ese tab no honra—, así el libro exportado no puede decir algo distinto de lo que se ve arriba.
   */
  const filtrosDeModeracion = useMemo(
    () => filterTags
      .filter((tag) => tag.key !== 'fundFilter')
      .map((tag) => tag.label)
      .join(' · ') || null,
    [filterTags],
  );

  /*
   * DEM-F4 (REPORTERIA-REGLAS §10, 2026-09-28) — al ENTRAR o SALIR de Demográfico la OU vuelve a su
   * valor inicial: la selección de una pestaña no se arrastra a la otra. Solo la OU: fechas,
   * Fundamental, Site y persona se siguen compartiendo (D-13). Para el Admin de OU, «valor inicial»
   * es su OU fija (`ouElegidas` la deriva cuando la barra queda vacía). Se hace en el clic y no en un
   * efecto: así el primer pintado de la pestaña nueva ya sale con la OU limpia.
   */
  function cambiarDePestana(siguiente: ReportTab) {
    if (siguiente !== activeTab && (siguiente === 'generational' || activeTab === 'generational')) {
      resetFilter('ou');
    }
    setActiveTab(siguiente);
  }

  // Mientras el alcance no esté resuelto no se monta ningún tab: cada uno dispara su propia
  // consulta con `params.ouIds` en cuanto se renderiza, y hacerlo antes de tiempo manda una
  // petición sin acotar por OU (el backend igual autoriza por rol — no es una fuga de datos —
  // pero es un viaje de más y un parpadeo de totales sin acotar). Mismo patrón que
  // `AnalyticsView/index.tsx`. Los hooks de arriba ya corrieron todos: este return es seguro
  // frente a las reglas de hooks.
  if (isResolving) return <div className="au-shell rr-shell"><PageSpinner /></div>;

  // AdminOU sin ninguna OU asignada dentro de su alcance: no hay nada que reportar.
  if (hasEmptyScope) {
    return (
      <div className="au-shell rr-shell">
        <p className="au-empty-scope">{t('reports.emptyScope')}</p>
      </div>
    );
  }

  return (
    <div className="au-shell rr-shell">
      <div className="au-tabs-outer">
        <div className="au-tabs-row">
          <div className="au-tabs-group" role="tablist" aria-label={t('reports.title')}>
            {tabsVisibles.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`au-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => cambiarDePestana(tab.id)}
              >
                <i className={`fas ${tab.icon}`} aria-hidden="true" />
                <span>{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/*
        ORDEN DE LA MAQUETA (`fundamentales-admin.js`, bloque `bodies.reporteria`), y el orden es el
        mensaje: pestañas → leyenda de homologación → tira de filtros → tags → «Mostrando» → plantilla.

        🔴 Nada de esto va ENCIMA de las pestañas. Antes sí, y leído de arriba abajo la pantalla decía
        «estos filtros mandan sobre lo que hay debajo», cuando en realidad Históricos los
        ignora. Con las pestañas primero, los filtros quedan visiblemente subordinados a la activa.
      */}
      {/*
        Paridad visual Fase 8 (2026-09-11) — `.rr-filters-card`: en la maqueta, `.fund-tab-panel` es
        UNA sola tarjeta translúcida que envuelve pestañas + filtros + banners; aquí estos 4 bloques
        flotaban sueltos sobre el fondo degradado de la página, incluido el banner verde de
        homologación. Se acota a este grupo (no a `.au-shell`/`.rr-shell` completo, ni a la plantilla,
        que ya tiene su propia tarjeta) para no reabrir todo el layout de la pantalla.
      */}
      {/*
        2026-09-29 (documento de Adriana, «filtros todos») — la plantilla sube a la parte superior,
        antes de la tarjeta de filtros. Sigue sin depender de la pestaña ni del rango (el censo es una
        foto del directorio; solo se acota por OU).
      */}
      <BloquePlantilla ouIds={ouIdsExpandidos} />

      <div className="rr-filters-card">
        {/* «Mostrando: …» arriba y destacado: es lo primero que hay que leer antes de una cifra. */}
        {showFilterBar && (
          <RecorteMostrado
            ouName={recorteDeOu}
            rango={filters.dateFrom && filters.dateTo ? `${isoADma(filters.dateFrom)} – ${isoADma(filters.dateTo)}` : null}
            fundamentalName={(() => {
              if (!filters.fundFilter) return null;
              const fund = catalog?.find((f) => f.id === filters.fundFilter);
              return fund ? getFundamentalName(fund, lang) : filters.fundFilter;
            })()}
          />
        )}
        <BannerHomologacion />

        {showFilterBar && (
          <>
            {/*
              Tira PROPIA de Reportería, en el orden de la maqueta. Sustituye a `FilterBar`, que sigue
              intacta para el módulo Analytics genérico (D-13): llegar a este orden habría exigido
              reordenar un componente compartido, y eso cambia una pantalla que nadie pidió tocar.
            */}
            <TiraDeFiltros
              filters={filters}
              setFilter={setFilter}
              resetFilters={resetFilters}
              fundamentales={fundamentalesDelFiltro}
              // BJS-1 (sesión 2026-10-02) — OU primero, luego estatus y campaña: la lista de campañas
              // depende de la OU.
              despuesDeOu={esBrillamos ? (
                <>
                  <SelectorCampanaReporte
                    estado={estadoCampana}
                    onEstado={(e) => { setEstadoCampana(e); setCampaignIdElegida(''); }}
                    campanas={campanasCombo}
                    campaignId={campanaElegida?.id ?? ''}
                    onCampana={elegirCampana}
                    ouItems={ouItemsCompletos}
                    cargando={campaigns.isLoading}
                    errorDeCarga={errorDeCampanas}
                  />
                </>
              ) : undefined}
              ouValue={ouElegidas}
              ouBloqueada={ouFilter.isLocked}
              personaVisible={personaAplica}
              sites={siteVisible ? sitesDelFiltro : null}
              siteValue={siteEfectivo}
              sitesCargando={sitesQuery.isLoading}
            />
            {/* 2026-10-02 — los avisos de la barra van DEBAJO del panel, en la fila de los chips
                («Fechas: …»): dentro de la tira se metían entre los controles y movían su orden. */}
            <div className="rr-tira-pie">
              <FilterTags tags={filterTags} />
              {/* 2026-09-29 (documento de Adriana) — los filtros de arriba acotan la LISTA de
                  campañas por OU y estatus; la persona no. El reporte de la campaña sí se filtra
                  por persona, así que un «no aplica en esta sección» sería falso: la nota dice
                  exactamente a qué no aplica. */}
              {/* 2026-10-15 (QA) — con campaña elegida ese aviso ya no viene al caso (la lista de
                  campañas no está en juego) y se leía como que la persona no filtra: se dice en su lugar
                  qué cuenta el reporte con la persona (BJC-5, BJC-6 y BJC-7). */}
              {esBrillamos && filters.persona && !campanaElegida && (
                <span className="rr-tira-nota" role="note" data-testid="persona-no-filtra-campanas">
                  <i className="fas fa-circle-info" aria-hidden="true" /> {t('reports.shine.personaNoFiltraCampanas')}
                </span>
              )}
              {esBrillamos && filters.persona && campanaElegida && (
                <span className="rr-tira-nota" role="note" data-testid="persona-en-el-reporte">
                  <i className="fas fa-circle-info" aria-hidden="true" /> {t('reports.shine.personaEnElReporte')}
                </span>
              )}
              {fechasDeLaCampana && (
                <span className="rr-tira-nota" role="note" data-testid="fechas-de-la-campana">
                  <i className="fas fa-circle-info" aria-hidden="true" /> {t('reports.shine.datesFromCampaign')}
                </span>
              )}
            </div>

            <BannersDeContexto canExportData={canExportData} />
          </>
        )}
      </div>


      <div className="au-tab-panel active" role="tabpanel">
        {activeTab === 'daily' && (
          <DailyTab
            params={params}
            canExportData={canExportData}
            ouNombresSeleccionados={ouNombresSeleccionados}
            ouIdsRecibidos={ouIdsRecibidos}
          />
        )}
        {activeTab === 'shine' && (
          <ShineTab
            params={params}
            canExportData={canExportData}
            campana={campanaElegida}
            alcanceVisible={alcanceCampana}
            campaignIds={idsDeTodas}
            avisoDeAlcance={avisoDeAlcance}
            onLimpiarCampana={() => setCampaignIdElegida('')}
          />
        )}
        {/*
          * Demográfico ABSORBE el mapa de calor y la red cross-OU: en la maqueta los tres viven en
          * `rep-panel-demografico`. El orden es el de la maqueta — primero la demografía, luego el
          * mapa «OU × Fundamental», y al final la colaboración entre OUs.
          */}
        {activeTab === 'generational' && puedeVerDemografico && (
          <>
            {/* C7 — export consolidado (3 hojas, 1 descarga) del panel completo, antes de sus 4
                bloques: no depende de cuál esté visible ni de scroll para encontrarlo. */}
            <DemograficoExportButton params={params} language={lang} canExportData={canExportData} />
            <GenerationalTab params={params} ouIds={ouElegidas} />
            {/* DEM-F5 (2026-09-28) — sin «Brecha Generacional × Fundamental»: salía de PROMEDIOS DE
                PULSO y no corresponde a esta sección (tampoco va en el Excel). */}
            {/*
              * C7 (ronda de revisión) — Mapa de Calor exige la capacidad `MapaDeCalor`
              * (`ViewAnalytics` + `PermissionScope.Global`, SIN `RestrictedToRoles` —
              * `CapabilityPolicy.cs`). `AdminGlobal` Y `AdminBrillamos` tienen `MaxScope: Global`
              * (`RoleCatalog.cs`) y reciben el MISMO trato en las 12 secciones de Reconocimiento
              * (`accessMatrix.ts` → `RECOGNITION_DIRECT_ACCESS`, incluida `reporteria`): el backend
              * autoriza a los dos con 200. Solo `AdminOUBrillamos` (`MaxScope: OuBu`) recibe 403
              * confirmado. Lista compartida con `DemograficoExportButton` en
              * `HEATMAP_ALLOWED_ROLES` (`reportsShared.tsx`) — ver ahí el detalle completo y por qué
              * NO se usa `orPermission` para esto.
              *
              * Independiente del RoleGuard de arriba (AdminGlobal/AdminBrillamos/AdminOUBrillamos),
              * que sigue cubriendo a Generacional y Red — esas dos siguen bajo `MapaCalorYRedCrossOu`,
              * que sí admite el acotado por OU/BU.
              */}
            <RoleGuard allow={HEATMAP_ALLOWED_ROLES}>
              <HeatmapTab params={params} />
            </RoleGuard>
            <NetworkTab params={params} />
          </>
        )}
        {activeTab === 'leaders' && (
          <LeadersTab params={params} canExportData={canExportData} ouNombresSeleccionados={ouNombresSeleccionados} />
        )}
        {activeTab === 'moderation' && (
          <ModerationReportTab
            params={params}
            filtrosAplicados={filtrosDeModeracion}
            canExportData={canExportData}
          />
        )}
        {activeTab === 'campaigns' && <CampaignsGeneralTab canExportData={canExportData} />}
      </div>
    </div>
  );
}

// FASE 3 (paridad demo) — antes montaba su propio UserProvider/ThemeProvider/GradientBackground/
// <main> (shell duplicado, sin sidebar). Ahora cuelga de <AppLayout/> como el resto de /app/*.
/** Ruta directa `/app/reportes` — gate por rol admin (analítica/reportería). */
export function RecognitionAnalyticsView() {
  const { t } = useTranslation('recognition');
  return (
    <div className="adm-shell">
      <header className="page-header adm-header">
        <div>
          <h1 className="page-title">{t('reports.title')}</h1>
          <p className="page-subtitle">{t('reports.subtitle')}</p>
        </div>
        <Link to="/app/admin/reporteria" className="adm-back">
          <i className="fas fa-arrow-left" aria-hidden="true" /> {t('admin.title')}
        </Link>
      </header>
      <div className="adm-body">
        {/* 2026-09-25 — AdminOU se agrega para que la ruta directa /app/reportes no contradiga a
            accessMatrix.canAccessRecognitionAdminSection('reporteria', ...), que ya lo garantiza
            (REPORTERIA_GUARANTEED_ROLES): sin esto, un AdminOU vería la pestaña en el menú de Admin
            pero un candado al entrar por este link directo. */}
        <RoleGuard
          allow={['AdminGlobal', 'AdminBrillamos', 'AdminOUBrillamos', 'AdminOU']}
          fallback={<div className="adm-denied" role="alert"><i className="fas fa-lock" /> {t('admin.denied')}</div>}
        >
          <RecognitionReports />
        </RoleGuard>
      </div>
    </div>
  );
}

export default RecognitionAnalyticsView;
